'use client';

import { RefCallback, useEffect, useState } from 'react';

import { getAppConfig } from '@services/config';
import { familyAdvantagesPredicate, getFamilyContract } from '@services/mcube';

import { getImpactSimulationServer } from '@actions/get-impacts';

import { MarketLineType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { Advantage } from '@app-types/simulator/simulator';
import { UserType } from '@app-types/user';

import { EligibleDiscounts, LoginPrompt } from '@components/connection';
import { LineType } from '@components/connection/lines';

import { formatPhoneNumber } from '@helpers/phone-number';
import { noop } from '@helpers/try-catch';

const appConfig = getAppConfig();

interface ConnectionIncentiveProps {
    user?: UserType;
    minOfferEligible?: MobilePlanType;
    ref?: RefCallback<HTMLElement>;
}

export function ConnectionIncentive({ user, minOfferEligible, ref }: Readonly<ConnectionIncentiveProps>) {
    const [proposedLines, setProposedLines] = useState<LineType[]>([]);
    const [eligibleLines, setEligibleLines] = useState<LineType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const refreshData = async (minOfferEligible?: MobilePlanType) => {
        try {
            const simulation = await getImpactSimulationServer([appConfig.defaultPlans.simulator.mobile]);

            if (simulation) {
                const eligible: LineType[] = getFamilyContract(simulation).map((contract) => ({
                    label:
                        formatPhoneNumber(contract.msisdn || contract.voipNumber) ||
                        contract.applicableOp?.commercialLabel,
                    discount:
                        contract.applicableOp.advantages?.filter(familyAdvantagesPredicate)[0]?.discountIncludingTax,
                    marketLine: contract.applicableOp.marketLine as MarketLineType,
                }));

                const mobileOffer = simulation?.proposed?.filter(
                    (prop) =>
                        prop.applicableOp?.advantages?.some((a) => familyAdvantagesPredicate(a)) &&
                        prop.applicableOp?.marketLine === MarketLineType.FNB,
                );
                let mobileDiscount: Advantage | undefined;
                if (mobileOffer) {
                    mobileDiscount = mobileOffer[0]?.applicableOp?.advantages?.find((a) =>
                        familyAdvantagesPredicate(a),
                    );
                }

                setProposedLines([
                    {
                        label: minOfferEligible?.dataEnvelope
                            ? `À partir de ${minOfferEligible?.dataEnvelope}`
                            : 'Votre nouveau forfait',
                        discount: mobileDiscount ? mobileDiscount.discountIncludingTax : undefined,
                        marketLine: MarketLineType.FNB,
                    },
                ]);

                setEligibleLines(eligible);
            }

            setIsLoading(false);
        } catch {
            setHasError(true);
        }
    };

    useEffect(() => {
        if (user) {
            refreshData(minOfferEligible).catch(noop);
        }
    }, [user?.sub, minOfferEligible]);

    if (!isLoading && hasError) {
        return null;
    }

    if (!user) {
        return <LoginPrompt />;
    }

    return (
        <div ref={ref}>
            <EligibleDiscounts
                user={user}
                proposedLines={proposedLines}
                proposedLinesLabel='Vous choisissez votre nouveau forfait'
                eligibleLines={eligibleLines}
                isLoading={isLoading}
            />
        </div>
    );
}
